import { DS_COLOR, DS_SHAPE_SPACING } from '@calm/evexia-design-tokens';
import isSSR from '@calm/web-common/dist/isSSR';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { useScroll } from '@react-spring/web';
import { useCurrentBreakpointName } from '@rhg/hooks/use-current-breakpoint-name';
import { useDisplayedCarouselProgram } from '@rhg/hooks/use-displayed-carousel-program';
import { useIntersectionObserver } from '@rhg/hooks/use-intersection-observer';
import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    List,
    ListItem,
    IconButton,
    Theme,
    ButtonBase,
    SxProps,
} from '@rhg/libs/material';
import { grey } from '@rhg/libs/material/colors';
import {
    NumberByBreakpoint,
    getValueByBreakpoint,
    styleNoScrollbar,
    sxAppend,
} from '@rhg/libs/material/sx';
import { WillBeNullProvider } from '@rhg/providers/will-be-null';
import { isSafari } from '@rhg/utils/browser';
import {
    Children,
    CSSProperties,
    MutableRefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { CarouselProps, CarouselListItemsProps } from './types';

const styleContainer = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
};
const styleList = {
    display: 'grid',
    overflowX: 'scroll',
    overflowY: 'hidden',
    padding: 0,
    overscrollBehaviorX: 'contain',
};
const styleScrollSnap = {
    scrollSnapType: 'x mandatory',
};
const stylePreviewList = { mr: 0 };
const styleItem = (width?: NumberByBreakpoint | number) => ({
    p: 0,
    m: 0,
    scrollSnapAlign: 'start',
    position: 'relative',
    flex: 0,
    // For some reason overriding the width itself doesn't seem to work
    ...(width ? { maxWidth: width, minWidth: width } : undefined),
});
const styleUnclickableItemOverlay = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: (theme: Theme) => theme.zIndex.fab - 1,
};
const styleArrowBackIosCorrector = {
    position: 'relative',
    left: `${DS_SHAPE_SPACING.BASE}px`,
};
const styleButton = {
    boxShadow: `0px 1px 4px 0px ${DS_COLOR.BLACK_12P}`,
    pointerEvents: 'auto',
    zIndex: (theme: Theme) => theme.zIndex.fab,
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'mask.white.60p' : 'mask.black.20p',
    '&:hover, &:focus': {
        backgroundColor: (theme: Theme) =>
            theme.palette.mode === 'dark' ? 'action.hover' : grey[100],
    },
};
const styleButtonInvisible = { visibility: 'hidden' };

const CONTAINER_WIDTH_FUDGE_FACTOR = 10;
const PROPORTION_OF_ITEM_TO_SHOW_BEFORE_CLICKABLE = 0.7;

const styleSpacingContainer = {
    position: 'relative',
};
const buttonSize = '40px';

const styleButtonContainer = {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'space-between',
    pointerEvents: 'none',
    width: {
        // 24px === mobile page padding value
        xs: `calc(100dvw - 24px - ${buttonSize})`,
        // 30px === 1.5 padding value
        md: `calc(100% + ${buttonSize} + 30px)`,
    },
    px: '20px',
    // md: double padding
    left: { xs: 0, md: '-40px' },
};
const styleButtonContainerInvisible = { opacity: 0 };

function CarouselSpacingContainer({
    children,
    itemSpacing,
    willRemoveFirstAndLastVisibleItemSpacing,
    wrapperSx,
    ...props
}: {
    children: React.ReactNode;
    itemSpacing?: number;
    willRemoveFirstAndLastVisibleItemSpacing?: boolean;
    wrapperSx?: SxProps<Theme>;
}) {
    return willRemoveFirstAndLastVisibleItemSpacing && itemSpacing ? (
        <Box sx={sxAppend(styleSpacingContainer, wrapperSx)} {...props}>
            {children}
        </Box>
    ) : (
        <span {...props} style={wrapperSx as CSSProperties}>
            {children}
        </span>
    );
}

function CarouselListItem({
    itemWidth,
    scrollRef,
    itemWidthValue,
    trackItemScrollButton,
    position,
    children,
}: CarouselListItemsProps) {
    const { formatMessage } = useIntl();
    const [isItemClickable, setIsItemClickable] = useState(true);
    const carouselItemRef = useRef<HTMLLIElement>(null);

    const displayedCarouselProgramContext = useDisplayedCarouselProgram();
    const intersectingCallback = useCallback(
        (entry: IntersectionObserverEntry) => {
            const _isItemClickable =
                entry.intersectionRatio >=
                PROPORTION_OF_ITEM_TO_SHOW_BEFORE_CLICKABLE;
            setIsItemClickable(_isItemClickable);
            if (_isItemClickable) {
                displayedCarouselProgramContext?.setDisplayedItemIndex(
                    position
                );
            }
        },
        [displayedCarouselProgramContext, position]
    );

    useIntersectionObserver({
        targetRef: carouselItemRef,
        intersectingCallback,
        nonIntersectingCallback: () => setIsItemClickable(false),
        observerOptions: {
            rootMargin: '0px',
            threshold: PROPORTION_OF_ITEM_TO_SHOW_BEFORE_CLICKABLE,
        },
    });

    const onItemClick = useCallback(() => {
        if (!isItemClickable) {
            scrollRef.current?.scrollTo({
                left: position * itemWidthValue,
                behavior: 'smooth',
            });
        }
    }, [isItemClickable, itemWidthValue, position, scrollRef]);

    return (
        <ListItem sx={styleItem(itemWidth)} ref={carouselItemRef}>
            {children}
            {!isItemClickable && (
                <ButtonBase
                    tabIndex={-1}
                    trackButton={trackItemScrollButton ?? 'NotMapped'}
                    sx={styleUnclickableItemOverlay}
                    onClick={onItemClick}
                    aria-label={formatMessage(
                        commonMessages['common.scroll_into_view']
                    )}
                />
            )}
        </ListItem>
    );
}

export function Carousel({
    itemWidth,
    itemSpacing,
    willRemoveFirstAndLastVisibleItemSpacing,
    itemsDisplayed = 1,
    showItemPreview,
    children,
    trackBackButton,
    trackForwardButton,
    trackItemScrollButton = 'NotMapped',
    withAccessibleButtons = true,
    showButtons = true,
    showButtonsWhenDisabled = false,
    toggleButtonsOnHover = false,
    firstItemOffsetPx = 0,
    buttonContainerSx,
    listSx,
    sx,
    emptyState,
    ...props
}: CarouselProps) {
    const scrollRef = useRef<HTMLUListElement>(null);
    const { formatMessage } = useIntl();
    const isSafariUser = isSafari();

    const [containerWidth, setContainerWidth] = useState(-1);
    const [scrollXProgress, setScrollXProgress] = useState(0);
    const [scrollX, setScrollX] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    const handleHoverStart = useCallback(() => {
        setIsHovered(true);
    }, []);

    const handleHoverEnd = useCallback(() => {
        setIsHovered(false);
    }, []);

    const containerRef = useRef<HTMLDivElement>(null);
    const observer = useRef(
        isSSR()
            ? undefined
            : new ResizeObserver((entries) => {
                  const { width } = entries[0].contentRect;
                  setContainerWidth(width);
              })
    );

    useEffect(() => {
        const currentObserver = observer.current;
        const currentContainer = containerRef.current;
        if (currentContainer) {
            currentObserver?.observe(currentContainer);
        }
        return () => {
            if (currentContainer) {
                currentObserver?.unobserve(currentContainer);
            }
        };
    }, []);

    const currentBreakpoint = useCurrentBreakpointName();

    const itemWidthValue = useMemo(
        () => getValueByBreakpoint<number>(itemWidth, currentBreakpoint) ?? 0,
        [currentBreakpoint, itemWidth]
    );

    const itemSpacingValue = useMemo(
        () =>
            getValueByBreakpoint<number>(itemSpacing ?? 0, currentBreakpoint) ??
            0,
        [currentBreakpoint, itemSpacing]
    );

    /**
     * This is used to properly align the first actual carousel item.
     * We do this by creating a blank invisible first item in the list with the specified width and padding.
     */
    const firstItemOffsetValue = useMemo(
        () => getValueByBreakpoint(firstItemOffsetPx, currentBreakpoint) ?? 0,
        [currentBreakpoint, firstItemOffsetPx]
    );

    const numItemsDisplayed = useMemo(() => {
        const maxItems =
            containerWidth < 0
                ? Number.MAX_VALUE
                : Math.floor(
                      (containerWidth + CONTAINER_WIDTH_FUDGE_FACTOR) /
                          itemWidthValue
                  );
        const desiredItems =
            typeof itemsDisplayed === 'number'
                ? itemsDisplayed
                : itemsDisplayed[currentBreakpoint];
        return Math.min(maxItems, desiredItems);
    }, [containerWidth, currentBreakpoint, itemWidthValue, itemsDisplayed]);

    useScroll({
        container: scrollRef as MutableRefObject<HTMLElement>,
        immediate: true,
        onChange: ({ value: { scrollX, scrollXProgress } }) => {
            setScrollXProgress(scrollXProgress);
            setScrollX(scrollX);
        },
    });

    const canMoveBack = useMemo(() => {
        return (
            scrollX > Number(firstItemOffsetValue) + itemWidthValue / 10 &&
            scrollXProgress > 0
        );
    }, [firstItemOffsetValue, itemWidthValue, scrollX, scrollXProgress]);

    const canMoveForward = useMemo(
        () => scrollXProgress < 1,
        [scrollXProgress]
    );

    const handleBack = useCallback(() => {
        scrollRef.current?.scrollTo({
            left:
                scrollRef.current?.scrollLeft -
                numItemsDisplayed * itemWidthValue,
            behavior: 'smooth',
        });
    }, [itemWidthValue, numItemsDisplayed]);

    const handleForward = useCallback(() => {
        scrollRef.current?.scrollTo({
            left:
                scrollRef.current?.scrollLeft +
                numItemsDisplayed * itemWidthValue,
            behavior: 'smooth',
        });
    }, [itemWidthValue, numItemsDisplayed]);

    const arrayChildren = Children.toArray(children);
    const [nonNullChildren, setNonNullChildren] = useState(
        new Set(arrayChildren.keys())
    );

    const handleWillBeNull = (index: number) => () => {
        setNonNullChildren((childrenSet) => {
            childrenSet.delete(index);
            return childrenSet;
        });
    };

    if (nonNullChildren.size === 0) {
        return <>{emptyState}</>;
    }

    return (
        <CarouselSpacingContainer
            itemSpacing={itemSpacingValue}
            willRemoveFirstAndLastVisibleItemSpacing={
                willRemoveFirstAndLastVisibleItemSpacing
            }
            {...props}
        >
            <Box
                sx={sxAppend(styleContainer, sx)}
                ref={containerRef}
                onMouseEnter={handleHoverStart}
                onMouseLeave={handleHoverEnd}
                onFocus={handleHoverStart}
                onBlur={handleHoverEnd}
            >
                {showButtons && (
                    <Box
                        aria-hidden={!withAccessibleButtons}
                        sx={sxAppend(
                            styleButtonContainer,
                            toggleButtonsOnHover &&
                                !isHovered &&
                                styleButtonContainerInvisible,
                            buttonContainerSx
                        )}
                    >
                        <IconButton
                            trackButton={trackBackButton}
                            onClick={handleBack}
                            color="primary"
                            tabIndex={withAccessibleButtons ? 0 : -1}
                            aria-label={formatMessage(
                                commonMessages['common.back']
                            )}
                            size={
                                currentBreakpoint === 'xs' ||
                                currentBreakpoint === 'sm'
                                    ? 'small'
                                    : 'medium'
                            }
                            disabled={!canMoveBack}
                            sx={sxAppend(
                                styleButton,
                                !canMoveBack &&
                                    !showButtonsWhenDisabled &&
                                    styleButtonInvisible
                            )}
                        >
                            <ArrowBackIos sx={styleArrowBackIosCorrector} />
                        </IconButton>
                        <IconButton
                            trackButton={trackForwardButton}
                            onClick={handleForward}
                            aria-label={formatMessage(
                                commonMessages['common.forward']
                            )}
                            color="primary"
                            tabIndex={withAccessibleButtons ? 0 : -1}
                            disabled={!canMoveForward}
                            size={
                                currentBreakpoint === 'xs' ||
                                currentBreakpoint === 'sm'
                                    ? 'small'
                                    : 'medium'
                            }
                            sx={sxAppend(
                                styleButton,
                                !canMoveForward &&
                                    !showButtonsWhenDisabled &&
                                    styleButtonInvisible
                            )}
                        >
                            <ArrowForwardIos />
                        </IconButton>
                    </Box>
                )}
                <List
                    ref={scrollRef}
                    sx={sxAppend(
                        styleList,
                        styleNoScrollbar,
                        {
                            scrollPaddingLeft: firstItemOffsetValue,
                            gridTemplateColumns: `repeat(${arrayChildren.length}, minmax(${itemWidthValue}px, 1fr))`,
                            gap: `${itemSpacingValue}px`,
                        },
                        showItemPreview && stylePreviewList,
                        !isSafariUser && styleScrollSnap,
                        listSx
                    )}
                    aria-label="carouselScrollContainer"
                >
                    {arrayChildren.map((child, i) => {
                        return (
                            <WillBeNullProvider
                                key={i}
                                handleWillBeNull={handleWillBeNull(i)}
                            >
                                <CarouselListItem
                                    position={i}
                                    itemWidth={itemWidth}
                                    scrollRef={scrollRef}
                                    itemWidthValue={itemWidthValue}
                                    trackItemScrollButton={
                                        trackItemScrollButton
                                    }
                                >
                                    {child}
                                </CarouselListItem>
                            </WillBeNullProvider>
                        );
                    })}
                </List>
            </Box>
        </CarouselSpacingContainer>
    );
}
